import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Smartphone } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { availableApps } from '@/config/appLinks';

function StoreQr({ url, label }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex flex-col items-center gap-1.5 group"
    >
      {/* QR modules are always dark-on-white; the white padded tile keeps
          them scannable in dark mode instead of inverting the code. */}
      <div className="p-2 bg-white rounded-lg border border-slate-200 group-hover:border-blue-300 transition-colors">
        <QRCodeSVG value={url} size={96} />
      </div>
      <span className="text-[10px] font-medium text-slate-500 dark:text-slate-400 group-hover:text-blue-600">
        {label}
      </span>
    </a>
  );
}

export default function AppDownloadMenu() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const apps = availableApps();
  if (apps.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        title={t('appLinks.tooltip')}
        aria-label={t('appLinks.tooltip')}
        className={`p-2 rounded-xl transition-all hover:bg-slate-100 dark:hover:bg-slate-800 ${
          open ? 'bg-slate-100 dark:bg-slate-800' : ''
        }`}
      >
        <Smartphone size={16} className="text-slate-500 dark:text-slate-400" />
      </button>

      {open && (
        <div className="absolute end-0 top-full mt-1.5 w-72 rounded-xl overflow-hidden shadow-xl z-50 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
          <div className="px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300">
              {t('appLinks.title')}
            </h4>
            <p className="text-[10px] text-slate-400 mt-0.5">{t('appLinks.scanHint')}</p>
          </div>
          {apps.map((app) => (
            <div
              key={app.id}
              className="px-3 py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
            >
              <div className="mb-2">
                <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {app.name}
                </span>
                <span className="ms-2 text-[10px] text-slate-400">{t(app.audienceKey)}</span>
              </div>
              <div className="flex items-start justify-center gap-6">
                {app.ios && <StoreQr url={app.ios} label={t('appLinks.appStore')} />}
                {app.android && <StoreQr url={app.android} label={t('appLinks.googlePlay')} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
